// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Pure, DOM-free analysis: histogram binning (positional-tested), decade means,
// and the auto-detected Insights. Imported by views and by the test suite.

import type { ClimateData, Station } from './types';
import { median } from './data';

export interface Bin { x0: number; x1: number; count: number; items: number[] }

/** Fixed-width histogram bins covering [min, max]. Each value lands in exactly one bin. */
export function histogram(values: number[], min: number, max: number, nBins: number): Bin[] {
  const vals = values.filter((v) => Number.isFinite(v));
  const width = (max - min) / nBins;
  const bins: Bin[] = Array.from({ length: nBins }, (_, i) => ({
    x0: min + i * width, x1: min + (i + 1) * width, count: 0, items: [],
  }));
  for (const v of vals) {
    let idx = Math.floor((v - min) / width);
    if (idx < 0) idx = 0;
    if (idx >= nBins) idx = nBins - 1; // clamp the max edge into the last bin
    bins[idx].count++;
    bins[idx].items.push(v);
  }
  return bins;
}

/** Mean of a per-year field over the first / last `n` valid years. */
export function decadeMeans(
  rows: Array<Record<string, number | null> & { y: number }>, field: string, n = 10,
): { early: number | null; late: number | null; earlyYears: [number, number]; lateYears: [number, number] } {
  const valid = rows.filter((r) => r[field] != null) as Array<{ y: number } & Record<string, number>>;
  const mean = (a: number[]) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : null);
  const first = valid.slice(0, n);
  const last = valid.slice(-n);
  return {
    early: first.length ? mean(first.map((r) => r[field] as number)) : null,
    late: last.length ? mean(last.map((r) => r[field] as number)) : null,
    earlyYears: first.length ? [first[0].y, first[first.length - 1].y] : [0, 0],
    lateYears: last.length ? [last[0].y, last[last.length - 1].y] : [0, 0],
  };
}

export type Severity = 'alert' | 'warn' | 'info';
export interface Insight { severity: Severity; title: string; body: string }

export function buildInsights(data: ClimateData): Insight[] {
  const { meta, national, stations } = data;
  const out: Insight[] = [];
  const rated = (s: Station) => s.rateMean != null;

  out.push({
    severity: 'alert',
    title: `Australia has warmed ${meta.nationalWarming.toFixed(2)} °C since ${meta.firstYear}`,
    body: `Averaged across all ${meta.stationCount} ACORN-SAT reference stations, the mean temperature has risen about ${meta.nationalWarming.toFixed(2)} °C — a trend of ${meta.nationalRate.toFixed(2)} °C every decade. That matches the Bureau of Meteorology's own State of the Climate figure of roughly 1.5 °C.`,
  });

  if (meta.nationalRateMin > meta.nationalRateMax) {
    out.push({
      severity: 'warn',
      title: 'Nights are warming faster than days',
      body: `Overnight minimum temperatures are rising ${meta.nationalRateMin.toFixed(2)} °C/decade, faster than daytime maximums at ${meta.nationalRateMax.toFixed(2)} °C/decade. Warmer nights give bodies, cities and ecosystems less chance to cool down.`,
    });
  }

  out.push({
    severity: 'alert',
    title: `${meta.hottestYear} was the hottest year on record`,
    body: `${meta.hottestYear} sat ${meta.hottestYearAnom.toFixed(2)} °C above the 1961–1990 average — the warmest in the ${meta.lastYear - meta.firstYear + 1}-year record. Nine of the ten hottest years have occurred since 2005.`,
  });

  const h35 = decadeMeans(national.annual as never, 'h35');
  if (h35.early != null && h35.late != null) {
    out.push({
      severity: 'warn',
      title: `Hot days (35 °C+) are up ${Math.round(((h35.late - h35.early) / h35.early) * 100)}%`,
      body: `The average station now records about ${h35.late.toFixed(0)} days at or above 35 °C a year (${h35.lateYears[0]}–${h35.lateYears[1]}), up from ${h35.early.toFixed(0)} a century ago (${h35.earlyYears[0]}–${h35.earlyYears[1]}).`,
    });
  }

  const frost = decadeMeans(national.annual as never, 'frost');
  if (frost.early != null && frost.late != null && frost.late < frost.early) {
    out.push({
      severity: 'info',
      title: `Frosty nights are becoming rarer`,
      body: `Nights at or below 2 °C have fallen from about ${frost.early.toFixed(0)} a year (${frost.earlyYears[0]}–${frost.earlyYears[1]}) to ${frost.late.toFixed(0)} (${frost.lateYears[0]}–${frost.lateYears[1]}) at the average station.`,
    });
  }

  const fastest = stations.filter(rated).slice().sort((a, b) => (b.rateMean as number) - (a.rateMean as number))[0];
  if (fastest) {
    out.push({
      severity: 'warn',
      title: `${fastest.name} is warming fastest`,
      body: `${fastest.name} (${fastest.state}) is warming at ${fastest.rateMean!.toFixed(2)} °C/decade — well above the national ${meta.nationalRate.toFixed(2)} °C/decade — a total of about ${fastest.totalMean?.toFixed(1)} °C over its record.`,
    });
  }

  const hottestStation = stations
    .filter((s) => s.recHigh)
    .sort((a, b) => (b.recHigh!.t) - (a.recHigh!.t))[0];
  if (hottestStation) {
    out.push({
      severity: 'info',
      title: `The hottest day in the record: ${hottestStation.recHigh!.t.toFixed(1)} °C`,
      body: `${hottestStation.name} (${hottestStation.state}) holds the highest single-day maximum in this homogenised record — ${hottestStation.recHigh!.t.toFixed(1)} °C. Homogenisation adjusts raw readings for site moves and equipment changes, so this differs slightly from raw station records.`,
    });
  }

  const rates = stations.filter(rated).map((s) => s.rateMean as number);
  const med = median(rates);
  const cooling = stations.filter((s) => s.rateMean != null && (s.rateMean as number) < 0.06);
  out.push({
    severity: 'info',
    title: `Almost every station is warming`,
    body: `Of the ${rates.length} stations with a full trend, the median warming rate is ${med.toFixed(2)} °C/decade and only ${cooling.length} sit below 0.06 °C/decade. Warming is near-universal across the network, not confined to cities.`,
  });

  return out;
}
